// src/components/admin/VolunteerSetupPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TenantConfigService from '../../services/TenantConfigService';

const VolunteerSetupPage = (props) => {
  const { currentUser } = props;
  const navigate = useNavigate();
  
  // ADD DEBUGGING IMMEDIATELY
  console.log('🔧 VolunteerSetupPage - Component Loading');
  console.log('🔧 VolunteerSetupPage - Props received:', props);
  console.log('🔧 VolunteerSetupPage - currentUser:', currentUser);
  console.log('🔧 VolunteerSetupPage - currentUser role:', currentUser?.role);
  console.log('🔧 VolunteerSetupPage - currentUser tenantId:', currentUser?.tenantId);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // General Volunteer Configuration
  const [generalConfig, setGeneralConfig] = useState({
    requireApproval: true,
    allowSelfSignup: true,
    allowVolunteerCancellation: true,
    cancellationDeadlineHours: 24,
    maxEventsPerVolunteer: 10,
    allowRecurringSignups: true,
    sendConfirmationEmails: true,
    sendReminderEmails: true,
    reminderHoursBefore: 24
  });

  // Event Management Configuration
  const [eventConfig, setEventConfig] = useState({
    allowWeekendEvents: true,
    allowHolidayEvents: false,
    defaultEventDuration: 2, // hours
    allowMultiDayEvents: true,
    requireEventDescription: true,
    allowEventCategories: true,
    maxAdvanceBookingDays: 365,
    minAdvanceNoticeHours: 2
  });

  // Event Categories
  const [eventCategories, setEventCategories] = useState([
    'General Volunteer Work',
    'Event Setup/Cleanup',
    'Administrative Support',
    'Community Outreach',
    'Special Events',
    'Maintenance & Grounds'
  ]);

  // Registration Form Configuration
  const [registrationConfig, setRegistrationConfig] = useState({
    enablePublicRegistration: true,
    requireBackgroundCheck: false,
    requireEmergencyContact: true,
    requireAvailability: false,
    requireSkillsSelection: false,
    enableCustomFields: true,
    requireTermsAcceptance: true,
    enableCaptcha: true
  });

  // Custom Registration Fields
  const [customFields, setCustomFields] = useState([
    {
      id: 'emergency_contact',
      name: 'Emergency Contact',
      type: 'text',
      required: true,
      isPreset: true
    },
    {
      id: 'skills_interests',
      name: 'Skills & Interests',
      type: 'textarea',
      required: false,
      isPreset: true
    }
  ]);

  // Notification Settings
  const [notificationConfig, setNotificationConfig] = useState({
    adminNotifications: {
      newRegistration: true,
      eventSignup: true,
      eventCancellation: true,
      volunteerNoShow: true
    },
    volunteerNotifications: {
      approvalStatus: true,
      eventReminders: true,
      eventUpdates: true,
      eventCancellation: true
    },
    emailTemplates: {
      welcomeEmail: {
        enabled: true,
        subject: 'Welcome to {organizationName} Volunteer Program!',
        includeNextSteps: true
      },
      reminderEmail: {
        enabled: true,
        subject: 'Reminder: Volunteer Event Tomorrow',
        includeEventDetails: true
      }
    }
  });

  // Role Configuration
  const [roleConfig, setRoleConfig] = useState({
    volunteerRolePermissions: {
      viewOwnProfile: true,
      editOwnProfile: true,
      viewAvailableEvents: true,
      signupForEvents: true,
      cancelOwnSignups: true,
      viewOwnHistory: true
    },
    managerRolePermissions: {
      manageAllVolunteers: true,
      createEvents: true,
      editEvents: true,
      deleteEvents: true,
      assignVolunteers: true,
      sendNotifications: true,
      viewReports: true,
      exportData: true
    }
  });

  // Skills/Interests Options
  const [skillsOptions, setSkillsOptions] = useState([
    'Administrative Work',
    'Event Planning',
    'Technology Support',
    'Communications',
    'Fundraising',
    'Manual Labor',
    'Teaching/Training',
    'Photography',
    'Social Media',
    'Transportation'
  ]);

  useEffect(() => {
    console.log('🔧 VolunteerSetupPage - useEffect triggered');
    console.log('🔧 VolunteerSetupPage - currentUser in useEffect:', currentUser);
    console.log('🔧 VolunteerSetupPage - currentUser?.tenantId:', currentUser?.tenantId);
    
    if (!currentUser || !currentUser.tenantId) {
      console.log('❌ VolunteerSetupPage - Missing currentUser or tenantId');
      console.log('❌ VolunteerSetupPage - currentUser:', currentUser);
      console.log('❌ VolunteerSetupPage - tenantId:', currentUser?.tenantId);
      setError('User not authenticated or missing tenant ID');
      setLoading(false);
      return;
    }
    
    console.log('✅ VolunteerSetupPage - About to load configurations');
    loadConfigurations(currentUser.tenantId);
  }, [currentUser]);

  const loadConfigurations = async (tenantId) => {
    console.log('🔧 VolunteerSetupPage - loadConfigurations called with tenantId:', tenantId);
    setLoading(true);
    try {
      console.log('🔧 VolunteerSetupPage - Starting to load configs...');
      
      // Load all volunteer configuration sections
      const [
        generalConfigData,
        eventConfigData,
        registrationConfigData,
        notificationConfigData,
        roleConfigData,
        eventCategoriesData,
        customFieldsData,
        skillsOptionsData
      ] = await Promise.all([
        TenantConfigService.getConfig(tenantId, 'volunteer_general', generalConfig),
        TenantConfigService.getConfig(tenantId, 'volunteer_events', eventConfig),
        TenantConfigService.getConfig(tenantId, 'volunteer_registration', registrationConfig),
        TenantConfigService.getConfig(tenantId, 'volunteer_notifications', notificationConfig),
        TenantConfigService.getConfig(tenantId, 'volunteer_roles', roleConfig),
        TenantConfigService.getConfig(tenantId, 'volunteer_categories', { categories: eventCategories }),
        TenantConfigService.getConfig(tenantId, 'volunteer_custom_fields', { fields: customFields }),
        TenantConfigService.getConfig(tenantId, 'volunteer_skills', { skills: skillsOptions })
      ]);

      setGeneralConfig(generalConfigData);
      setEventConfig(eventConfigData);
      setRegistrationConfig(registrationConfigData);
      setNotificationConfig(notificationConfigData);
      setRoleConfig(roleConfigData);
      setEventCategories(eventCategoriesData.categories || eventCategories);
      setCustomFields(customFieldsData.fields || customFields);
      setSkillsOptions(skillsOptionsData.skills || skillsOptions);

      console.log('✅ VolunteerSetupPage - Configurations loaded successfully');

    } catch (error) {
      console.error('❌ VolunteerSetupPage - Error loading configurations:', error);
      setError('Failed to load volunteer configurations. Please try again.');
    } finally {
      setLoading(false);
      console.log('🔧 VolunteerSetupPage - Loading complete, loading state set to false');
    }
  };

  // Save Functions
  const saveGeneralConfig = async () => {
    if (!currentUser?.tenantId) {
      setError('No tenant ID found. Please refresh and try again.');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_general', 
        generalConfig
      );

      if (success) {
        setSuccessMessage('General volunteer settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save general volunteer settings.');
      }
    } catch (error) {
      console.error('Error saving general config:', error);
      setError('Error saving general volunteer settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveEventConfig = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_events', 
        eventConfig
      );

      if (success) {
        setSuccessMessage('Event management settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save event management settings.');
      }
    } catch (error) {
      console.error('Error saving event config:', error);
      setError('Error saving event management settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveEventCategories = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const validCategories = eventCategories.filter(cat => cat.trim());
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_categories', 
        { categories: validCategories }
      );

      if (success) {
        setSuccessMessage('Event categories saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save event categories.');
      }
    } catch (error) {
      console.error('Error saving event categories:', error);
      setError('Error saving event categories. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveRegistrationConfig = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_registration', 
        registrationConfig
      );

      if (success) {
        setSuccessMessage('Registration settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save registration settings.');
      }
    } catch (error) {
      console.error('Error saving registration config:', error);
      setError('Error saving registration settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveCustomFields = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const validFields = customFields.filter(field => field.id && field.name);
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_custom_fields', 
        { fields: validFields }
      );

      if (success) {
        setSuccessMessage('Custom fields saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save custom fields.');
      }
    } catch (error) {
      console.error('Error saving custom fields:', error);
      setError('Error saving custom fields. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveNotificationConfig = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_notifications', 
        notificationConfig
      );

      if (success) {
        setSuccessMessage('Notification settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save notification settings.');
      }
    } catch (error) {
      console.error('Error saving notification config:', error);
      setError('Error saving notification settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveRoleConfig = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_roles', 
        roleConfig
      );

      if (success) {
        setSuccessMessage('Role settings saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save role settings.');
      }
    } catch (error) {
      console.error('Error saving role config:', error);
      setError('Error saving role settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const saveSkillsOptions = async () => {
    if (!currentUser?.tenantId) return;
    
    setSaving(true);
    setError('');
    
    try {
      const validSkills = skillsOptions.filter(skill => skill.trim());
      const success = await TenantConfigService.updateConfig(
        currentUser.tenantId, 
        'volunteer_skills', 
        { skills: validSkills }
      );

      if (success) {
        setSuccessMessage('Skills options saved successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to save skills options.');
      }
    } catch (error) {
      console.error('Error saving skills options:', error);
      setError('Error saving skills options. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Helper Functions
  const addEventCategory = () => {
    setEventCategories([...eventCategories, '']);
  };

  const updateEventCategory = (index, value) => {
    const updated = [...eventCategories];
    updated[index] = value;
    setEventCategories(updated);
  };

  const removeEventCategory = (index) => {
    const updated = eventCategories.filter((_, i) => i !== index);
    setEventCategories(updated);
  };

  const addCustomField = () => {
    const newField = {
      id: `custom_${Date.now()}`,
      name: '',
      type: 'text',
      required: false,
      isPreset: false
    };
    setCustomFields([...customFields, newField]);
  };

  const updateCustomField = (index, field, value) => {
    const updated = [...customFields];
    updated[index][field] = value;
    setCustomFields(updated);
  };

  const removeCustomField = (index) => {
    const field = customFields[index];
    if (field.isPreset) return; // Don't remove preset fields
    
    const updated = customFields.filter((_, i) => i !== index);
    setCustomFields(updated);
  };

  const addSkillOption = () => {
    setSkillsOptions([...skillsOptions, '']);
  };

  const updateSkillOption = (index, value) => {
    const updated = [...skillsOptions];
    updated[index] = value;
    setSkillsOptions(updated);
  };

  const removeSkillOption = (index) => {
    const updated = skillsOptions.filter((_, i) => i !== index);
    setSkillsOptions(updated);
  };

  // Add debugging to the render method
  console.log('🔧 VolunteerSetupPage - Rendering component');
  console.log('🔧 VolunteerSetupPage - Current loading state:', loading);
  console.log('🔧 VolunteerSetupPage - Current error state:', error);

  if (loading) {
    console.log('🔧 VolunteerSetupPage - Showing loading screen');
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h1>Volunteer Setup</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>⏳ Loading volunteer configuration...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we load your volunteer management settings.</div>
        </div>
      </div>
    );
  }

  console.log('🔧 VolunteerSetupPage - Showing main content');

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      {/* Add debugging info at the top temporarily */}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ margin: 0, color: '#007bff' }}>🤝 Volunteer Management Setup</h1>
        <button 
          onClick={() => {
            console.log('🔧 Back button clicked, navigating to /admin');
            navigate('/admin');
          }}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '8px 16px',
            cursor: 'pointer'
          }}
        >
          ← Back to Admin
        </button>
      </div>

      {/* Success/Error Messages */}
      {error && (
        <div style={{ 
          color: '#721c24', 
          backgroundColor: '#f8d7da', 
          border: '1px solid #f5c6cb', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontWeight: 'bold'
        }}>
          ⚠️ {error}
        </div>
      )}
      {successMessage && (
        <div style={{ 
          color: '#155724', 
          backgroundColor: '#d4edda', 
          border: '1px solid #c3e6cb', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontWeight: 'bold'
        }}>
          ✅ {successMessage}
        </div>
      )}

      {/* General Volunteer Settings */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          ⚙️ General Volunteer Settings
        </h2>
        
        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>🤝 Volunteer Management Overview</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Configure core volunteer management settings including approval workflows, signup limits, and basic operational parameters.
            These settings control how volunteers interact with your organization's event system.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Key Features →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>Approval Workflow:</strong> Control whether new volunteer registrations require manual approval</li>
              <li><strong>Self-Service Signups:</strong> Allow volunteers to sign up for events independently</li>
              <li><strong>Cancellation Management:</strong> Set rules for when and how volunteers can cancel commitments</li>
              <li><strong>Capacity Controls:</strong> Limit how many events each volunteer can sign up for</li>
              <li><strong>Automated Reminders:</strong> Send confirmation and reminder emails automatically</li>
            </ul>
          </details>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
          {/* Approval & Signup Settings */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>📋 Approval & Signup Settings</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.requireApproval}
                onChange={(e) => setGeneralConfig({ ...generalConfig, requireApproval: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require manual approval for new volunteers</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.allowSelfSignup}
                onChange={(e) => setGeneralConfig({ ...generalConfig, allowSelfSignup: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow volunteers to self-signup for events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.allowRecurringSignups}
                onChange={(e) => setGeneralConfig({ ...generalConfig, allowRecurringSignups: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow recurring event signups</span>
            </label>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Maximum events per volunteer
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={generalConfig.maxEventsPerVolunteer}
                onChange={(e) => setGeneralConfig({ ...generalConfig, maxEventsPerVolunteer: parseInt(e.target.value) || 10 })}
                style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <small style={{ color: '#6c757d', marginLeft: '10px' }}>Active events at one time</small>
            </div>
          </div>

          {/* Cancellation & Communication Settings */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>📧 Cancellation & Communication</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.allowVolunteerCancellation}
                onChange={(e) => setGeneralConfig({ ...generalConfig, allowVolunteerCancellation: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow volunteers to cancel their signups</span>
            </label>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Cancellation deadline (hours before event)
              </label>
              <input
                type="number"
                min="0"
                max="168"
                value={generalConfig.cancellationDeadlineHours}
                onChange={(e) => setGeneralConfig({ ...generalConfig, cancellationDeadlineHours: parseInt(e.target.value) || 24 })}
                style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <small style={{ color: '#6c757d', marginLeft: '10px' }}>Set to 0 for no deadline</small>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.sendConfirmationEmails}
                onChange={(e) => setGeneralConfig({ ...generalConfig, sendConfirmationEmails: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Send confirmation emails</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={generalConfig.sendReminderEmails}
                onChange={(e) => setGeneralConfig({ ...generalConfig, sendReminderEmails: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Send reminder emails</span>
            </label>

            {generalConfig.sendReminderEmails && (
              <div style={{ marginLeft: '20px', marginBottom: '15px' }}>
                <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                  Send reminders (hours before event)
                </label>
                <input
                  type="number"
                  min="1"
                  max="168"
                  value={generalConfig.reminderHoursBefore}
                  onChange={(e) => setGeneralConfig({ ...generalConfig, reminderHoursBefore: parseInt(e.target.value) || 24 })}
                  style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={saveGeneralConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save General Settings'}
        </button>
      </div>

      {/* Event Management Configuration */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          📅 Event Management Configuration
        </h2>

        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>📊 Event Scheduling Rules</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Configure when and how volunteer events can be scheduled. Unlike substitute management which is limited to school days,
            volunteer events can be scheduled 7 days a week including weekends and holidays.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Event Flexibility →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>Weekend Events:</strong> Schedule volunteer work on Saturdays and Sundays</li>
              <li><strong>Holiday Events:</strong> Optional - allow events during holidays</li>
              <li><strong>Multi-Day Events:</strong> Support events spanning multiple days</li>
              <li><strong>Advanced Booking:</strong> Control how far in advance events can be scheduled</li>
              <li><strong>Last-Minute Events:</strong> Set minimum notice requirements</li>
            </ul>
          </details>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
          {/* Scheduling Rules */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>📆 Scheduling Rules</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={eventConfig.allowWeekendEvents}
                onChange={(e) => setEventConfig({ ...eventConfig, allowWeekendEvents: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow weekend events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={eventConfig.allowHolidayEvents}
                onChange={(e) => setEventConfig({ ...eventConfig, allowHolidayEvents: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow holiday events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={eventConfig.allowMultiDayEvents}
                onChange={(e) => setEventConfig({ ...eventConfig, allowMultiDayEvents: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Allow multi-day events</span>
            </label>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Default event duration (hours)
              </label>
              <input
                type="number"
                min="0.5"
                max="24"
                step="0.5"
                value={eventConfig.defaultEventDuration}
                onChange={(e) => setEventConfig({ ...eventConfig, defaultEventDuration: parseFloat(e.target.value) || 2 })}
                style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
          </div>

          {/* Booking & Notice Requirements */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>⏰ Booking & Notice Requirements</h4>
            
            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Maximum advance booking (days)
              </label>
              <input
                type="number"
                min="1"
                max="730"
                value={eventConfig.maxAdvanceBookingDays}
                onChange={(e) => setEventConfig({ ...eventConfig, maxAdvanceBookingDays: parseInt(e.target.value) || 365 })}
                style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <small style={{ color: '#6c757d', marginLeft: '10px' }}>How far in advance to allow scheduling</small>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                Minimum advance notice (hours)
              </label>
              <input
                type="number"
                min="0"
                max="168"
                value={eventConfig.minAdvanceNoticeHours}
                onChange={(e) => setEventConfig({ ...eventConfig, minAdvanceNoticeHours: parseInt(e.target.value) || 2 })}
                style={{ width: '100px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
              <small style={{ color: '#6c757d', marginLeft: '10px' }}>Minimum time before event can start</small>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={eventConfig.requireEventDescription}
                onChange={(e) => setEventConfig({ ...eventConfig, requireEventDescription: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require event descriptions</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={eventConfig.allowEventCategories}
                onChange={(e) => setEventConfig({ ...eventConfig, allowEventCategories: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Use event categories</span>
            </label>
          </div>
        </div>

        <button
          type="button"
          onClick={saveEventConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save Event Configuration'}
        </button>
      </div>

      {/* Event Categories */}
      {eventConfig.allowEventCategories && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            🏷️ Event Categories
          </h2>

          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>📂 Organize Events by Category</h4>
            <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
              Categories help volunteers find events that match their interests and skills. Create categories that reflect 
              the types of volunteer work your organization needs.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            {eventCategories.map((category, index) => (
              <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => updateEventCategory(index, e.target.value)}
                  placeholder="Category name"
                  style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <button
                  type="button"
                  onClick={() => removeEventCategory(index)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={addEventCategory}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: 'pointer'
              }}
            >
              + Add Category
            </button>
            <button
              type="button"
              onClick={saveEventCategories}
              disabled={saving}
              style={{
                backgroundColor: saving ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Categories'}
            </button>
          </div>
        </div>
      )}

      {/* Registration Configuration */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          📝 Volunteer Registration Configuration
        </h2>

        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>🌐 Public Registration Features</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Configure the public-facing volunteer registration form and requirements. This form allows community members 
            to sign up as volunteers for your organization.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Security & Compliance Features →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>CAPTCHA Protection:</strong> Prevent spam registrations with reCAPTCHA</li>
              <li><strong>Background Checks:</strong> Require background check information</li>
              <li><strong>Emergency Contacts:</strong> Collect emergency contact information</li>
              <li><strong>Terms Acceptance:</strong> Require acceptance of terms and conditions</li>
              <li><strong>Custom Fields:</strong> Collect organization-specific information</li>
            </ul>
          </details>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
          {/* Basic Registration Settings */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>🔓 Basic Registration Settings</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.enablePublicRegistration}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, enablePublicRegistration: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Enable public volunteer registration</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.requireEmergencyContact}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, requireEmergencyContact: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require emergency contact</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.requireAvailability}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, requireAvailability: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require availability schedule</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.requireSkillsSelection}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, requireSkillsSelection: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require skills & interests</span>
            </label>
          </div>

          {/* Security & Compliance */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>🔒 Security & Compliance</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.requireBackgroundCheck}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, requireBackgroundCheck: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require background check info</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.requireTermsAcceptance}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, requireTermsAcceptance: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Require terms & conditions</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.enableCaptcha}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, enableCaptcha: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Enable CAPTCHA protection</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={registrationConfig.enableCustomFields}
                onChange={(e) => setRegistrationConfig({ ...registrationConfig, enableCustomFields: e.target.checked })}
              />
              <span style={{ fontWeight: 'bold' }}>Enable custom fields</span>
            </label>
          </div>
        </div>

        <button
          type="button"
          onClick={saveRegistrationConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save Registration Settings'}
        </button>
      </div>

      {/* Custom Registration Fields */}
      {registrationConfig.enableCustomFields && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            📋 Custom Registration Fields
          </h2>

          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>🎯 Collect Organization-Specific Information</h4>
            <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
              Add custom fields to gather additional information during volunteer registration. These fields appear on the 
              public registration form and help you collect data specific to your organization's needs.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            {customFields.map((field, index) => (
              <div key={field.id} style={{ 
                border: '1px solid #ddd', 
                borderRadius: '8px', 
                padding: '15px', 
                marginBottom: '15px', 
                backgroundColor: field.isPreset ? '#f8f9fa' : '#fff'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 80px 60px', gap: '15px', alignItems: 'end' }}>
                  <div>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>
                      Field ID {field.isPreset && <span style={{ color: '#6c757d' }}>(Preset)</span>}
                    </label>
                    <input
                      type="text"
                      value={field.id}
                      onChange={(e) => updateCustomField(index, 'id', e.target.value)}
                      disabled={field.isPreset}
                      style={{ 
                        width: '100%', 
                        padding: '8px', 
                        borderRadius: '4px', 
                        border: '1px solid #ccc',
                        backgroundColor: field.isPreset ? '#f8f9fa' : 'white'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Field Name</label>
                    <input
                      type="text"
                      value={field.name}
                      onChange={(e) => updateCustomField(index, 'name', e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Type</label>
                    <select
                      value={field.type}
                      onChange={(e) => updateCustomField(index, 'type', e.target.value)}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="select">Select</option>
                      <option value="checkbox">Checkbox</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Required</label>
                    <div style={{ display: 'flex', justifyContent: 'center', height: '34px', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) => updateCustomField(index, 'required', e.target.checked)}
                        style={{ transform: 'scale(1.5)' }}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="button"
                      onClick={() => removeCustomField(index)}
                      disabled={field.isPreset}
                      style={{
                        backgroundColor: field.isPreset ? '#ccc' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 10px',
                        cursor: field.isPreset ? 'not-allowed' : 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={addCustomField}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: 'pointer'
              }}
            >
              + Add Custom Field
            </button>
            <button
              type="button"
              onClick={saveCustomFields}
              disabled={saving}
              style={{
                backgroundColor: saving ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Custom Fields'}
            </button>
          </div>
        </div>
      )}

      {/* Skills & Interests Options */}
      {registrationConfig.requireSkillsSelection && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
            🛠️ Skills & Interests Options
          </h2>

          <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#004085' }}>💼 Match Volunteers with Opportunities</h4>
            <p style={{ margin: '0', fontSize: '14px', color: '#004085' }}>
              Define skill and interest categories that volunteers can select during registration. This helps match 
              volunteers with events that suit their abilities and preferences.
            </p>
          </div>

          <div style={{ marginBottom: '20px' }}>
            {skillsOptions.map((skill, index) => (
              <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={skill}
                  onChange={(e) => updateSkillOption(index, e.target.value)}
                  placeholder="Skill or interest area"
                  style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
                <button
                  type="button"
                  onClick={() => removeSkillOption(index)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={addSkillOption}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: 'pointer'
              }}
            >
              + Add Skill/Interest
            </button>
            <button
              type="button"
              onClick={saveSkillsOptions}
              disabled={saving}
              style={{
                backgroundColor: saving ? '#ccc' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '10px 15px',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Skills Options'}
            </button>
          </div>
        </div>
      )}

      {/* Notification Configuration */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          📧 Notification Configuration
        </h2>

        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>📨 Automated Communication System</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Configure automatic email notifications for administrators and volunteers. Keep everyone informed about 
            registrations, event signups, cancellations, and important updates.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Notification Types →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>Admin Alerts:</strong> New registrations, event signups, no-shows</li>
              <li><strong>Volunteer Updates:</strong> Approval status, event reminders, changes</li>
              <li><strong>Welcome Emails:</strong> Automated onboarding for new volunteers</li>
              <li><strong>Event Reminders:</strong> Customizable timing and content</li>
            </ul>
          </details>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
          {/* Admin Notifications */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>👤 Admin Notifications</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.adminNotifications.newRegistration}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  adminNotifications: {
                    ...notificationConfig.adminNotifications,
                    newRegistration: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>New volunteer registration</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.adminNotifications.eventSignup}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  adminNotifications: {
                    ...notificationConfig.adminNotifications,
                    eventSignup: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Event signup</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.adminNotifications.eventCancellation}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  adminNotifications: {
                    ...notificationConfig.adminNotifications,
                    eventCancellation: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Event cancellation</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.adminNotifications.volunteerNoShow}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  adminNotifications: {
                    ...notificationConfig.adminNotifications,
                    volunteerNoShow: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Volunteer no-show</span>
            </label>
          </div>

          {/* Volunteer Notifications */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>🤝 Volunteer Notifications</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.volunteerNotifications.approvalStatus}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  volunteerNotifications: {
                    ...notificationConfig.volunteerNotifications,
                    approvalStatus: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Approval status updates</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.volunteerNotifications.eventReminders}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  volunteerNotifications: {
                    ...notificationConfig.volunteerNotifications,
                    eventReminders: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Event updates</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={notificationConfig.volunteerNotifications.eventCancellation}
                onChange={(e) => setNotificationConfig({
                  ...notificationConfig,
                  volunteerNotifications: {
                    ...notificationConfig.volunteerNotifications,
                    eventCancellation: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Event cancellation notifications</span>
            </label>
          </div>
        </div>

        {/* Email Templates Section */}
        <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '20px', marginTop: '20px' }}>
          <h4 style={{ color: '#495057', marginBottom: '15px' }}>📧 Email Templates</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
            {/* Welcome Email */}
            <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
              <h5 style={{ margin: '0 0 15px 0', color: '#007bff' }}>Welcome Email</h5>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="checkbox"
                  checked={notificationConfig.emailTemplates.welcomeEmail.enabled}
                  onChange={(e) => setNotificationConfig({
                    ...notificationConfig,
                    emailTemplates: {
                      ...notificationConfig.emailTemplates,
                      welcomeEmail: {
                        ...notificationConfig.emailTemplates.welcomeEmail,
                        enabled: e.target.checked
                      }
                    }
                  })}
                />
                <span style={{ fontWeight: 'bold' }}>Enable welcome emails</span>
              </label>

              {notificationConfig.emailTemplates.welcomeEmail.enabled && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Subject Line</label>
                    <input
                      type="text"
                      value={notificationConfig.emailTemplates.welcomeEmail.subject}
                      onChange={(e) => setNotificationConfig({
                        ...notificationConfig,
                        emailTemplates: {
                          ...notificationConfig.emailTemplates,
                          welcomeEmail: {
                            ...notificationConfig.emailTemplates.welcomeEmail,
                            subject: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      placeholder="Welcome to {organizationName}!"
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={notificationConfig.emailTemplates.welcomeEmail.includeNextSteps}
                      onChange={(e) => setNotificationConfig({
                        ...notificationConfig,
                        emailTemplates: {
                          ...notificationConfig.emailTemplates,
                          welcomeEmail: {
                            ...notificationConfig.emailTemplates.welcomeEmail,
                            includeNextSteps: e.target.checked
                          }
                        }
                      })}
                    />
                    <span>Include next steps information</span>
                  </label>
                </>
              )}
            </div>

            {/* Reminder Email */}
            <div style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px' }}>
              <h5 style={{ margin: '0 0 15px 0', color: '#007bff' }}>Reminder Email</h5>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <input
                  type="checkbox"
                  checked={notificationConfig.emailTemplates.reminderEmail.enabled}
                  onChange={(e) => setNotificationConfig({
                    ...notificationConfig,
                    emailTemplates: {
                      ...notificationConfig.emailTemplates,
                      reminderEmail: {
                        ...notificationConfig.emailTemplates.reminderEmail,
                        enabled: e.target.checked
                      }
                    }
                  })}
                />
                <span style={{ fontWeight: 'bold' }}>Enable reminder emails</span>
              </label>

              {notificationConfig.emailTemplates.reminderEmail.enabled && (
                <>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ fontWeight: 'bold', marginBottom: '5px', display: 'block' }}>Subject Line</label>
                    <input
                      type="text"
                      value={notificationConfig.emailTemplates.reminderEmail.subject}
                      onChange={(e) => setNotificationConfig({
                        ...notificationConfig,
                        emailTemplates: {
                          ...notificationConfig.emailTemplates,
                          reminderEmail: {
                            ...notificationConfig.emailTemplates.reminderEmail,
                            subject: e.target.value
                          }
                        }
                      })}
                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                      placeholder="Reminder: Volunteer Event Tomorrow"
                    />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={notificationConfig.emailTemplates.reminderEmail.includeEventDetails}
                      onChange={(e) => setNotificationConfig({
                        ...notificationConfig,
                        emailTemplates: {
                          ...notificationConfig.emailTemplates,
                          reminderEmail: {
                            ...notificationConfig.emailTemplates.reminderEmail,
                            includeEventDetails: e.target.checked
                          }
                        }
                      })}
                    />
                    <span>Include full event details</span>
                  </label>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={saveNotificationConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer',
            marginTop: '20px'
          }}
        >
          {saving ? 'Saving...' : 'Save Notification Settings'}
        </button>
      </div>

      {/* Role Configuration */}
      <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          👥 Role & Permission Configuration
        </h2>

        <div style={{ backgroundColor: '#e7f3ff', border: '1px solid #b8daff', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#004085' }}>🔐 Access Control & Permissions</h4>
          <p style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#004085' }}>
            Define what volunteers and volunteer managers can do within the system. These permissions control 
            access to features and data throughout the volunteer management module.
          </p>
          <details style={{ color: '#004085' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '8px' }}>Permission Categories →</summary>
            <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px' }}>
              <li><strong>Volunteer Self-Service:</strong> Profile management, event browsing, signup controls</li>
              <li><strong>Manager Oversight:</strong> User management, event creation, reporting access</li>
              <li><strong>Data Access:</strong> View own vs. view all data permissions</li>
              <li><strong>Administrative Actions:</strong> Export data, send notifications</li>
            </ul>
          </details>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '20px' }}>
          {/* Volunteer Permissions */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>🤝 Volunteer Role Permissions</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.viewOwnProfile}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    viewOwnProfile: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>View own profile</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.editOwnProfile}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    editOwnProfile: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Edit own profile</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.viewAvailableEvents}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    viewAvailableEvents: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>View available events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.signupForEvents}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    signupForEvents: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Sign up for events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.cancelOwnSignups}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    cancelOwnSignups: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Cancel own signups</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.volunteerRolePermissions.viewOwnHistory}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  volunteerRolePermissions: {
                    ...roleConfig.volunteerRolePermissions,
                    viewOwnHistory: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>View own event history</span>
            </label>
          </div>

          {/* Manager Permissions */}
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>👨‍💼 Manager Role Permissions</h4>
            
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.manageAllVolunteers}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    manageAllVolunteers: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Manage all volunteers</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.createEvents}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    createEvents: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Create events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.editEvents}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    editEvents: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Edit events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.deleteEvents}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    deleteEvents: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Delete events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.assignVolunteers}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    assignVolunteers: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Assign volunteers to events</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.sendNotifications}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    sendNotifications: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Send notifications</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.viewReports}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    viewReports: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>View reports</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', padding: '8px', border: '1px solid #dee2e6', borderRadius: '4px' }}>
              <input
                type="checkbox"
                checked={roleConfig.managerRolePermissions.exportData}
                onChange={(e) => setRoleConfig({
                  ...roleConfig,
                  managerRolePermissions: {
                    ...roleConfig.managerRolePermissions,
                    exportData: e.target.checked
                  }
                })}
              />
              <span style={{ fontWeight: 'bold' }}>Export data</span>
            </label>
          </div>
        </div>

        <button
          type="button"
          onClick={saveRoleConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            cursor: saving ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Saving...' : 'Save Role Configuration'}
        </button>
      </div>

      {/* Quick Actions Summary */}
      <div style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
        <h2 style={{ margin: '0 0 20px 0', color: '#007bff', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
          🚀 Quick Actions & Next Steps
        </h2>

        <div style={{ backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '6px', padding: '15px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#155724' }}>✅ Configuration Complete!</h4>
          <p style={{ margin: '0', fontSize: '14px', color: '#155724' }}>
            Your volunteer management system is now configured. Here are your next steps to get volunteers engaged:
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>📝 Administrative Tasks</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#6c757d' }}>
              <li style={{ marginBottom: '8px' }}>Set up your first volunteer events</li>
              <li style={{ marginBottom: '8px' }}>Review and approve volunteer registrations</li>
              <li style={{ marginBottom: '8px' }}>Test email notifications</li>
              <li style={{ marginBottom: '8px' }}>Train volunteer managers on the system</li>
            </ul>
          </div>
          <div>
            <h4 style={{ color: '#495057', marginBottom: '15px' }}>🌐 Public Outreach</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#6c757d' }}>
              <li style={{ marginBottom: '8px' }}>Share volunteer registration link</li>
              <li style={{ marginBottom: '8px' }}>Promote upcoming events</li>
              <li style={{ marginBottom: '8px' }}>Collect feedback from early volunteers</li>
              <li style={{ marginBottom: '8px' }}>Monitor registration spam protection</li>
            </ul>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '20px', marginTop: '20px', textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/volunteer-events')}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 20px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📅 Create First Event
            </button>
            <button
              onClick={() => navigate('/volunteer-approvals')}
              style={{
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 20px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              👥 Manage Volunteers
            </button>
            <button
              onClick={() => navigate('/volunteer-dashboard')}
              style={{
                backgroundColor: '#ffc107',
                color: 'black',
                border: 'none',
                borderRadius: '4px',
                padding: '12px 20px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              📊 View Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VolunteerSetupPage;